import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Avatar,
  TextField,
  Chip,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  DialogActions,
  useTheme,
  Tooltip,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  InputAdornment,
} from "@mui/material";
import dummyAvatar from "../../../assets/avatar.png";
import { Formik, Form, Field } from "formik";
import { sampleUser } from "./sampleData";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import {
  editImage,
  fetchTradePreferenceOptions,
} from "../../../features/user/userSlice";
import { FaMagic } from "react-icons/fa";
import { FaEraser, FaImage, FaWandMagicSparkles } from "react-icons/fa6";
import { COUNTRY_CODES } from "../../../utils/countryCodes";

const splitPhone = (rawPhone = "") => {
  if (rawPhone.startsWith("+")) {
    const sorted = [...COUNTRY_CODES].sort(
      (a, b) => b.code.length - a.code.length,
    );
    const match = sorted.find((c) => rawPhone.startsWith(c.code));
    if (match) {
      return {
        countryCode: match.code,
        localNumber: rawPhone.slice(match.code.length),
      };
    }
  }
  return { countryCode: "+61", localNumber: rawPhone };
};

//  Yup validation schema
const ProfileSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  phone: Yup.string()
    .matches(/^[0-9]+$/, "Must be only digits")
    .min(7, "Too short")
    .required("Phone number is required"),
  communication_phone: Yup.string()
    .nullable()
    .matches(/^[0-9]*$/, "Must be only digits")
    .min(10, "Too short"),
  company_name: Yup.string().required("Company name is required"),
  communication_email: Yup.string().nullable().email("Invalid email address"),
});

const EditProfileTab = ({ user, tradePreference, onSave }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { tradeOptions = [], editImageLoading } = useSelector(
    (state) => state.user,
  );

  const [previewUrl, setPreviewUrl] = useState(null);
  const [showAiModal, setShowAiModal] = useState(false);
  const [tempAiPreview, setTempAiPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // const [profileData, setProfileData] = useState({
  //   name: "",
  //   phone: "",
  //   company: "",
  //   profileImage: null,
  //   tradePreferences: [],
  // });

  useEffect(() => {
    dispatch(fetchTradePreferenceOptions()).unwrap().catch(console.error);
  }, [dispatch]);

  useEffect(() => {
    console.log("tempAiPreview updated:", tempAiPreview);
  }, [tempAiPreview]);

  // Cleanup on unmount to prevent state leakage between users
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      setSelectedFile(null);
      setTempAiPreview(null);
    };
  }, []);

  const { countryCode: derivedCode, localNumber: derivedLocal } = React.useMemo(
    () => splitPhone(user?.phone || ""),
    [user?.phone],
  );

  const initialValues = {
    name: user?.name || "",
    phone: derivedLocal, // local digits only  e.g. "412345678"
    countryCode: derivedCode, // e.g. "+61"
    // communication_phone: user?.communication_phone || "",
    // communication_email: user?.communication_email || "",
    // is_email_visible: user?.is_email_visible ?? 0,
    // is_phone_visible: user?.is_phone_visible ?? 0,
    company_name: user?.company_name || "",
    // store IDs in formik state
    trade_preferences: Array.isArray(tradePreference)
      ? tradePreference.map((item) =>
          typeof item === "object" ? Number(item.id) : Number(item),
        )
      : [],
    // file is chosen by the user; existing URL comes from user
    profile_image: null,
  };

  const handleAiEdit = async (action) => {
    try {
      let fileToSend = selectedFile || previewUrl || user?.profile_image_url;
      if (!fileToSend) return;

      if (typeof fileToSend === "string") {
        const res = await fetch(fileToSend);
        const blob = await res.blob();
        fileToSend = new File([blob], "profile.png", { type: blob.type });
      }

      const formData = new FormData();
      formData.append("image", fileToSend);
      formData.append("action", action);

      const result = await dispatch(editImage(formData)).unwrap();
      console.log("AI edit result in EditProfileTab:", result);

      const editedUrl =
        result?.data?.edited_image || result?.data?.image_base64;
      if (editedUrl) {
        let finalUrl = editedUrl;
        // If it looks like raw base64 (no data: prefix and no http), add the prefix
        if (
          typeof editedUrl === "string" &&
          !editedUrl.startsWith("http") &&
          !editedUrl.startsWith("data:") &&
          !editedUrl.startsWith("blob:")
        ) {
          finalUrl = `data:image/png;base64,${editedUrl}`;
        }

        console.log(
          "Setting tempAiPreview to:",
          finalUrl.substring(0, 50) + "...",
        );
        setTempAiPreview(finalUrl);
      }
    } catch (err) {
      console.error("AI edit failed:", err);
    }
  };

  const applyAiPreview = async (setFieldValue) => {
    if (!tempAiPreview) return;

    try {
      console.log("Applying AI preview URL:", tempAiPreview);
      const res = await fetch(tempAiPreview);
      const blob = await res.blob();
      const file = new File([blob], "ai_profile.png", { type: blob.type });
      console.log("Converted AI preview to File object:", file);

      // 1. Update form state (so it gets saved)
      setFieldValue("profile_image", file);

      // 2. Update local tracking
      setSelectedFile(file);
      setPreviewUrl(tempAiPreview);

      // 3. Clear temp state and close modal
      setTempAiPreview(null);
      setShowAiModal(false);
    } catch (err) {
      console.error("Failed to apply AI preview:", err);
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={ProfileSchema}
        onSubmit={(values, helpers) => {
          console.log("Submitting Profile with values:", values);
          onSave(values, helpers);
        }}
        validateOnBlur
        validateOnChange
      >
        {({ values, setFieldValue, errors, touched, isSubmitting }) => {
          // Priority:
          // 1. previewUrl (AI or manual selection)
          // 2. values.profile_image (if it's a blob/file from manual select but not yet in previewUrl)
          // 3. user?.profile_image_url (existing)
          // 4. Default
          const currentAvatarSrc =
            previewUrl ||
            (values.profile_image instanceof File
              ? URL.createObjectURL(values.profile_image)
              : null) ||
            user?.profile_image_url ||
            null;
          // sampleUser.profilePicture;

          const getInitials = (name) => {
            if (!name) return "";
            return name
              .trim()
              .split(" ")
              .map((word) => word.charAt(0).toUpperCase())
              .join("");
          };
          return (
            <>
              <Form>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {/* Profile Image Section */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    {/* <Avatar
                      src={currentAvatarSrc}
                      sx={{
                        width: 120,
                        height: 120,
                        border: "1px solid #ddd",
                        "& img": {
                          objectFit: "cover",
                          width: "100%",
                          height: "100%",
                        },
                      }}
                    /> */}
                    <Avatar
                      src={currentAvatarSrc}
                      sx={{
                        width: 120,
                        height: 120,
                        border: "1px solid #ddd",
                        bgcolor: theme.palette.scraptradeYellow.main, // shows when no src
                        fontSize: 40,
                        "& img": {
                          objectFit: "cover",
                          width: "100%",
                          height: "100%",
                        },
                      }}
                    >
                      {!currentAvatarSrc && getInitials(user?.name)}
                    </Avatar>

                    {/* Buttons Row */}
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant="outlined"
                        component="label"
                        sx={{
                          color: theme.palette.scraptradeYellow.main,
                          borderColor: theme.palette.scraptradeYellow.main,
                        }}
                      >
                        Choose Profile Image
                        <input
                          type="file"
                          hidden
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (file) {
                              console.log("Manually selected file:", file);
                              setFieldValue("profile_image", file);
                              setSelectedFile(file);
                              const url = URL.createObjectURL(file);
                              setPreviewUrl((prev) => {
                                if (prev) URL.revokeObjectURL(prev);
                                return url;
                              });
                            }
                          }}
                        />
                      </Button>

                      {/* <Tooltip
                        title={
                          <span
                            style={{ display: "block", textAlign: "center" }}
                          >
                            Use AI to enhance or remove background from your
                            profile picture
                          </span>
                        }
                        placement="bottom"
                      >
                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={() => setShowAiModal(true)}
                          sx={{
                            bgcolor: theme.palette.scraptradeYellow.main,
                            "&:hover": {
                              bgcolor: theme.palette.scraptradeYellow.dark,
                            },
                          }}
                        >
                          <FaWandMagicSparkles style={{ marginRight: 5 }} /> AI
                          Photo Editor
                        </Button>
                      </Tooltip> */}
                    </Box>

                    {touched.profile_image && errors.profile_image && (
                      <Typography variant="caption" color="error">
                        {errors.profile_image}
                      </Typography>
                    )}
                  </Box>

                  {/* Name */}
                  <Field
                    as={TextField}
                    name="name"
                    label="Name"
                    variant="outlined"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    fullWidth
                  />

                  {/* Phone */}
                  <Field name="phone">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        label="Phone"
                        variant="outlined"
                        fullWidth
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        error={meta.touched && Boolean(meta.error)}
                        helperText={meta.touched && meta.error}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Select
                                value={values.countryCode}
                                onChange={(e) =>
                                  setFieldValue("countryCode", e.target.value)
                                }
                                variant="standard"
                                disableUnderline
                                MenuProps={{
                                  PaperProps: {
                                    sx: { maxHeight: 260, borderRadius: 0 },
                                  },
                                }}
                                sx={{
                                  fontSize: "0.95rem",
                                  minWidth: 72,
                                  mr: 0.5,
                                  "& .MuiSelect-select": {
                                    py: 0,
                                    pr: "24px !important",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  },
                                }}
                                // Render only the dial code in the collapsed box
                                renderValue={(selected) => (
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {selected}
                                  </Typography>
                                )}
                              >
                                {COUNTRY_CODES.map((country) => (
                                  <MenuItem
                                    key={country.value}
                                    value={country.code}
                                    sx={{ gap: 1.5 }}
                                  >
                                    {/* Full label + code shown in dropdown */}
                                    <Typography
                                      variant="body2"
                                      sx={{ flexGrow: 1 }}
                                    >
                                      {country.label}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ flexShrink: 0 }}
                                    >
                                      {country.code}
                                    </Typography>
                                  </MenuItem>
                                ))}
                              </Select>

                              {/* Thin divider between selector and input */}
                              <Box
                                sx={{
                                  width: "1px",
                                  height: 22,
                                  bgcolor: "divider",
                                  mr: 1,
                                }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  </Field>

                  {/* <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Field
                      as={TextField}
                      name="communication_email"
                      label="Communication Email"
                      variant="outlined"
                      error={
                        touched.communication_email &&
                        Boolean(errors.communication_email)
                      }
                      helperText={
                        touched.communication_email &&
                        errors.communication_email
                      }
                      fullWidth
                    />

                    <CommunicationToggle
                      checked={Boolean(values.is_email_visible)}
                      onChange={(checked) =>
                        setFieldValue("is_email_visible", checked ? 1 : 0)
                      }
                    />
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Field
                      as={TextField}
                      name="communication_phone"
                      label="Communication Phone"
                      variant="outlined"
                      error={
                        touched.communication_phone &&
                        Boolean(errors.communication_phone)
                      }
                      helperText={
                        touched.communication_phone &&
                        errors.communication_phone
                      }
                      fullWidth
                    />

                    <CommunicationToggle
                      checked={Boolean(values.is_phone_visible)}
                      onChange={(checked) =>
                        setFieldValue("is_phone_visible", checked ? 1 : 0)
                      }
                    />
                  </Box> */}

                  {/* Company */}
                  <Field
                    as={TextField}
                    name="company_name"
                    label="Company"
                    variant="outlined"
                    error={touched.company_name && Boolean(errors.company_name)}
                    helperText={touched.company_name && errors.company_name}
                    fullWidth
                  />

                  {/* Trade Preferences (IDs) */}
                  <FormControl fullWidth>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Trade Preferences
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {(tradeOptions || []).map((pref) => {
                        const isSelected = values.trade_preferences.includes(
                          pref.id,
                        );
                        return (
                          <Chip
                            key={pref.id}
                            label={pref.name}
                            clickable
                            onClick={() => {
                              const updated = isSelected
                                ? values.trade_preferences.filter(
                                    (id) => id !== pref.id,
                                  )
                                : [...values.trade_preferences, pref.id];
                              setFieldValue("trade_preferences", updated);
                            }}
                            color={isSelected ? "primary" : "default"}
                            variant={isSelected ? "filled" : "outlined"}
                            sx={{
                              backgroundColor: isSelected
                                ? theme.palette.scraptradeYellow.main
                                : "#e0e0e0",
                              color: isSelected
                                ? "#fff"
                                : "rgba(0, 0, 0, 0.87)",
                              "&:hover": {
                                backgroundColor: isSelected
                                  ? theme.palette.scraptradeYellow.dark
                                  : "#d5d5d5",
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                  </FormControl>

                  {/* Save Button */}
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{
                      bgcolor: theme.palette.scraptradeYellow.main,
                      "&:hover": {
                        bgcolor: theme.palette.scraptradeYellow.dark,
                      },
                      px: 3,
                      alignSelf: "flex-end",
                    }}
                  >
                    {isSubmitting ? "Saving..." : "Save Changes"}
                  </Button>
                </Box>
              </Form>

              {/* AI Modal - Moved inside Formik to access setFieldValue */}
              <Dialog
                open={showAiModal}
                onClose={() => setShowAiModal(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                  sx: {
                    borderRadius: 3,
                    p: 2,
                    bgcolor: "#f9f9f9",
                  },
                }}
              >
                <DialogTitle
                  sx={{ textAlign: "center", fontWeight: "bold", fontSize: 20 }}
                >
                  AI Image Editor
                </DialogTitle>

                <DialogContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    mt: 2,
                    mb: 2,
                  }}
                >
                  {/* Dual Preview Row */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 3,
                      justifyContent: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    {/* Original Preview */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: "bold", color: "#666" }}
                      >
                        Original
                      </Typography>
                      <Avatar
                        src={
                          (selectedFile && URL.createObjectURL(selectedFile)) ||
                          previewUrl ||
                          user?.profile_image_url ||
                          dummyAvatar
                        }
                        variant="rounded"
                        sx={{
                          width: 180,
                          height: 180,
                          border: "2px solid #ddd",
                          borderRadius: 2,
                          boxShadow: 1,
                        }}
                      />
                    </Box>

                    {/* AI Preview */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: "bold",
                          color: theme.palette.scraptradeYellow.main,
                        }}
                      >
                        AI Preview
                      </Typography>
                      <Box sx={{ position: "relative" }}>
                        <img
                          src={tempAiPreview || dummyAvatar}
                          alt="AI Preview"
                          onError={(e) => {
                            console.error(
                              "AI Preview Failed to load. URL:",
                              e.target.src,
                            );
                            // e.target.src = dummyAvatar; // Fallback if it fails
                          }}
                          style={{
                            width: 180,
                            height: 180,
                            objectFit: "cover",
                            border: `2px solid ${theme.palette.scraptradeYellow.main}`,
                            borderRadius: 8,
                            boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                            filter: editImageLoading ? "blur(4px)" : "none",
                            transition: "filter 0.3s ease",
                            backgroundColor: "#f0f0f0",
                          }}
                        />

                        {editImageLoading && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              borderRadius: 2,
                              background: "rgba(0,0,0,0.1)",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <CircularProgress color="inherit" size={30} />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* AI Action Buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      mt: 2,
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      variant="contained"
                      disabled={editImageLoading}
                      onClick={() => handleAiEdit("enhance")}
                      startIcon={<FaImage />}
                      sx={{
                        backgroundImage: `linear-gradient(90deg, ${theme.palette.scraptradeYellow.main}, ${theme.palette.scraptradeYellow.dark})`,
                        color: "#000",
                        "&:hover": {
                          backgroundImage: `linear-gradient(90deg, ${theme.palette.scraptradeYellow.main}, ${theme.palette.scraptradeYellow.dark})`,
                        },
                      }}
                    >
                      Enhance
                    </Button>
                    <Button
                      variant="contained"
                      disabled={editImageLoading}
                      onClick={() => handleAiEdit("remove_bg")}
                      startIcon={<FaEraser />}
                      sx={{
                        backgroundImage: `linear-gradient(90deg, ${theme.palette.scraptradeYellow.main}, ${theme.palette.scraptradeYellow.dark})`,
                        color: "#000",
                        "&:hover": {
                          backgroundImage: `linear-gradient(90deg, ${theme.palette.scraptradeYellow.main}, ${theme.palette.scraptradeYellow.dark})`,
                        },
                      }}
                    >
                      Remove BG
                    </Button>
                  </Box>
                </DialogContent>

                {/* Accept / Cancel Buttons */}
                <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 2 }}>
                  <Button
                    onClick={() => {
                      setTempAiPreview(null);
                      setShowAiModal(false);
                    }}
                    color="warning"
                    variant="outlined"
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={!tempAiPreview}
                    variant="contained"
                    onClick={() => applyAiPreview(setFieldValue)}
                    sx={{
                      bgcolor: `linear-gradient(90deg, ${theme.palette.scraptradeYellow.main}, ${theme.palette.scraptradeYellow.dark})`,
                      "&:hover": {
                        bgcolor: `linear-gradient(90deg, ${theme.palette.scraptradeYellow.main}, ${theme.palette.scraptradeYellow.dark})`,
                      },
                    }}
                  >
                    Apply
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          );
        }}
      </Formik>
    </>
  );
};

export default EditProfileTab;

const CommunicationToggle = ({ checked, onChange }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 1,
        borderRadius: 2,
        border: "1px solid",
        borderColor: checked
          ? theme.palette.scraptradeYellow.main
          : theme.palette.divider,
        backgroundColor: checked
          ? theme.palette.scraptradeYellow.light
          : theme.palette.action.hover,
        transition: "all 0.2s ease",
        maxWidth: 260,
        flexShrink: 0,
      }}
    >
      <Switch
        size="small"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        sx={{
          "& .MuiSwitch-switchBase.Mui-checked": {
            color: theme.palette.scraptradeYellow.main,
          },
          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
            backgroundColor: theme.palette.scraptradeYellow.main,
          },
        }}
      />

      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: checked
            ? theme.palette.scraptradeYellow.dark
            : theme.palette.text.secondary,
          lineHeight: 1.2,
          wordBreak: "break-word",
        }}
      >
        Use for primary communication
      </Typography>
    </Box>
  );
};
